import React, { useEffect } from 'react';

function ReleaseInput({ releaseData, setReleaseData }) {
  // Handler for input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setReleaseData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Dynamically set the releaseTitle based on the version number
  useEffect(() => {
    if (releaseData.version) {
      setReleaseData((prevData) => ({
        ...prevData,
        releaseTitle: `tarnData Version ${releaseData.version} Release Notes`,
      }));
    }
  }, [releaseData.version, setReleaseData]);

  const setCurrentDateTime = () => {
    const estOffset = -5; // EST is UTC-5
    const currentDate = new Date();

    // Convert UTC to EST
    const estDate = new Date(currentDate.getTime() + estOffset * 60 * 60 * 1000);

    // Format the date to match "YYYY-MM-DDTHH:MM" (datetime-local input format)
    const formattedDate = estDate.toISOString().slice(0, 16);

    setReleaseData((prevData) => ({
      ...prevData,
      date: formattedDate,
    }));
  };

  // Add a new item to a section based on type (text or image)
  const addItem = (sectionIndex, itemType) => {
    const newItem = itemType === 'text' ? { text: '' } : { image: '' };
    const updatedSections = [...releaseData.sections];
    updatedSections[sectionIndex].items.push(newItem);
    setReleaseData((prevData) => ({
      ...prevData,
      sections: updatedSections,
    }));
  };

  // Remove an item from a section
  const removeItem = (sectionIndex, itemIndex) => {
    const updatedSections = [...releaseData.sections];
    updatedSections[sectionIndex].items.splice(itemIndex, 1);
    setReleaseData((prevData) => ({
      ...prevData,
      sections: updatedSections,
    }));
  };

  const handleSectionChange = (sectionIndex, itemIndex, key, value) => {
    const updatedSections = [...releaseData.sections];
    updatedSections[sectionIndex].items[itemIndex][key] = value;
    setReleaseData((prevData) => ({
      ...prevData,
      sections: updatedSections,
    }));
  };

  return (
    <div className="w-full p-4 sm:p-8 pt-14 text-sm">
      {/* Flexbox Container for Version and Date */}
      <div className="flex gap-4 mb-4 items-end">
        <label className="flex-1">
          <span className="block text-gray-700 font-medium mb-2">Version</span>
          <input
            type="text"
            name="version"
            value={releaseData.version}
            onChange={handleChange}
            className="w-full p-2 border rounded-md"
          />
        </label>
        <label className="flex-1 relative">
          <span className="block text-gray-700 font-medium mb-2">Date</span>
          <div className="flex">
            <input
              type="datetime-local"
              name="date"
              value={releaseData.date}
              onChange={handleChange}
              className="w-full p-2 border rounded-md"
            />
            <button
              type="button"
              onClick={setCurrentDateTime}
              className="ml-2 text-xs bg-blue-500 text-white px-2 rounded hover:bg-blue-700"
            >
              Now
            </button>
          </div>
        </label>
      </div>

      {/* Display Release Title */}
      <div className="mb-4">
        <span className="text-gray-700 font-medium">Release Title:</span>
        <span className="text-gray-900 ml-2">{releaseData.releaseTitle || 'No Title'}</span>
      </div>

      <label className="block mb-4">
        <span className="block text-gray-700 font-medium mb-2">Description</span>
        <textarea
          name="description"
          value={releaseData.description}
          onChange={handleChange}
          rows="4"
          className="w-full p-2 border rounded-md"
        />
      </label>

      {releaseData.sections.map((section, sectionIndex) => (
        <div key={sectionIndex} className="mb-6 border-t pt-4">
          <h3 className="text-xl font-semibold mb-2">{section.title}</h3>
          {section.items.map((item, itemIndex) => (
            <div key={itemIndex} className="mb-4 flex items-center">
              {item.text !== undefined && (
                <textarea
                  className="w-full p-2 border rounded-md mr-2"
                  placeholder="Enter text"
                  value={item.text}
                  onChange={(e) => handleSectionChange(sectionIndex, itemIndex, 'text', e.target.value)}
                />
              )}
              {item.image !== undefined && (
                <input
                  type="text"
                  placeholder="Image URL"
                  className="w-full p-2 border rounded-md mr-2"
                  value={item.image}
                  onChange={(e) => handleSectionChange(sectionIndex, itemIndex, 'image', e.target.value)}
                />
              )}
              {/* Remove item button */}
              <button
                onClick={() => removeItem(sectionIndex, itemIndex)}
                className="bg-red-500 text-xs text-white px-2 py-1 rounded hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          ))}
          {/* Select item type and add item button */}
          <div className="flex text-xs gap-4 mt-4">
            <button
              onClick={() => addItem(sectionIndex, 'text')}
              className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-700"
            >
              Add Text
            </button>
            <button
              onClick={() => addItem(sectionIndex, 'image')}
              className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-700"
            >
              Add Image
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default ReleaseInput;